import { useCallback, useEffect, useState } from 'react';

type StorageType = 'localStorage' | 'sessionStorage';

function useSessionStorage<T>(
  key: string,
  initialValue?: T,
  type: StorageType = 'sessionStorage'
) {
  const isBrowser = typeof window !== 'undefined';
  const storage = isBrowser ? window[type] : null;

  const [storedValue, setStoredValue] = useState<T | undefined>(() => {
    if (!isBrowser) return initialValue;
    try {
      const item = storage?.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  // Handle cross-tab and manual updates
  useEffect(() => {
    if (!isBrowser) return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.storageArea === storage) {
        try {
          const newValue = event.newValue
            ? JSON.parse(event.newValue)
            : initialValue;
          setStoredValue(newValue);
        } catch {
          setStoredValue(initialValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, initialValue, storage]);

  const setValue = useCallback(
    (value: T | ((val: T | undefined) => T)) => {
      try {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);
        storage?.setItem(key, JSON.stringify(valueToStore));

        // Optional: trigger synthetic 'storage' event for same-tab listeners
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new StorageEvent('storage', {
              key,
              newValue: JSON.stringify(valueToStore),
              storageArea: storage!,
            })
          );
        }
      } catch (error) {
        console.error(`Error setting ${type} key "${key}"`, error);
      }
    },
    [key, storedValue, storage, type]
  );

  return [storedValue, setValue] as const;
}

export default useSessionStorage;
